import React, { useEffect, useMemo, useState } from "react";
import { listFolderItems, TcFolderItem } from "../trimble/coreFiles";

type Props = {
  open: boolean;
  accessToken: string;
  projectId: string;
  rootFolderId: string;
  onCancel: () => void;
  onPick: (folder: { id: string; name?: string; path?: string }) => void | Promise<void>
};

export const TrimbleFolderPicker: React.FC<Props> = ({
  open,
  accessToken,
  projectId,
  rootFolderId,
  onCancel,
  onPick,
}) => {
  const [currentFolderId, setCurrentFolderId] = useState(rootFolderId);
  const [stack, setStack] = useState<string[]>([]);
  const [folders, setFolders] = useState<TcFolderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCurrentFolderId(rootFolderId);
    setStack([]);
  }, [open, rootFolderId]);

  useEffect(() => {
    if (!open) return;

    const run = async () => {
      try {
        setLoading(true);
        setErr(null);

        const items = await listFolderItems(accessToken, currentFolderId, projectId);
        setFolders(items.filter((i) => String(i.type).toUpperCase() === "FOLDER"));
      } catch (e) {
        setErr((e as Error).message ?? String(e));
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [open, accessToken, currentFolderId, projectId]);

  const canGoUp = useMemo(() => stack.length > 0, [stack]);

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9999 }}>
      <div style={{ width: 520, margin: "10vh auto", background: "#fff", borderRadius: 10, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Select Trimble Connect Folder</h3>

        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => {
              if (!canGoUp) return;
              const prev = stack[stack.length - 1];
              setStack(stack.slice(0, -1));
              setCurrentFolderId(prev);
            }}
            disabled={!canGoUp || loading}
          >
            Up
          </button>

          <button
            onClick={() => onPick({ id: currentFolderId })}
            disabled={loading}
          >
            Use this folder
          </button>

          <div style={{ marginLeft: "auto" }}>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>

        {loading && <div>Loading folders…</div>}
        {err && <div style={{ color: "crimson" }}>{err}</div>}

        <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd", borderRadius: 8 }}>
          {folders.map((f) => (
            <div
              key={f.id}
              style={{ padding: "10px 12px", borderBottom: "1px solid #eee", cursor: "pointer" }}
              onClick={() => {
                setStack([...stack, currentFolderId]);
                setCurrentFolderId(f.id);
              }}
            >
              {f.name}
            </div>
          ))}
          {!loading && folders.length === 0 && <div style={{ padding: 12, color: "#666" }}>No subfolders.</div>}
        </div>
      </div>
    </div>
  );
};
